import { Fragment, useEffect, useState } from 'react';
import {
  VIEWER_TZ_GROUPS,
  formatNowClockLabel,
  formatViewerTzOptionLabel,
  getBrowserTimezone,
  loadViewerTzPreference,
  saveViewerTzPreference,
} from '../utils/scheduleDates';

/** Тикает раз в 30 с — для часов и линии «сейчас». */
export function useScheduleNowTick() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * Часы «сейчас» + опциональный выбор пояса зрителя.
 * @param {object} props
 * @param {boolean} [props.showTimezoneSelect] — ученик / публичная страница
 * @param {string} [props.viewerTz] — controlled preference
 * @param {(tz: string) => void} [props.onViewerTzChange]
 * @param {Date} [props.now] — внешний тик (чтобы линия и часы совпадали)
 */
export default function ScheduleViewerClock({
  showTimezoneSelect = false,
  viewerTz: controlledTz,
  onViewerTzChange,
  now: nowProp,
}) {
  const [internalTz, setInternalTz] = useState(() => loadViewerTzPreference());
  const preference = controlledTz ?? internalTz;
  const internalNow = useScheduleNowTick();
  const now = nowProp ?? internalNow;

  function setPreference(next) {
    saveViewerTzPreference(next);
    if (onViewerTzChange) onViewerTzChange(next);
    else setInternalTz(next);
  }

  const label = formatNowClockLabel(now, preference);
  const browserTz = getBrowserTimezone();

  return (
    <div className="sched-viewer-clock">
      <span className="sched-now-clock" aria-live="polite">
        {label}
      </span>
      {showTimezoneSelect && (
        <label className="sched-filter sched-tz-filter">
          Ваш часовой пояс
          <select
            value={preference}
            onChange={(e) => setPreference(e.target.value)}
          >
            {VIEWER_TZ_GROUPS.map((group) => {
              const options = group.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {formatViewerTzOptionLabel(o, { now, browserTz })}
                </option>
              ));
              if (!group.label) {
                return <Fragment key="browser">{options}</Fragment>;
              }
              return (
                <optgroup key={group.label} label={group.label}>
                  {options}
                </optgroup>
              );
            })}
          </select>
          <span className="sched-tz-hint muted">Сетка по московскому времени преподавателя</span>
        </label>
      )}
    </div>
  );
}

/** Горизонтальная линия «сейчас» внутри колонки дня. */
export function ScheduleNowLine({ topPx }) {
  if (topPx == null || Number.isNaN(topPx)) return null;
  return (
    <div
      className="sched-now-line"
      style={{ top: `${topPx}px` }}
      aria-hidden
    />
  );
}
